import React, { useState, useEffect } from 'react';
import * as atoms from '../utils/atom';
import { Button, Form, message, Select, AutoComplete, notification, Tooltip } from 'antd';
import { useAtomValue } from 'jotai';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Dimension, CreateDimensionType, SelectItem, CREATE_OPERATION } from '../utils/types';
import * as Style from '../utils/styled';
import * as Const from '../utils/const';
import { getCustomTagAutoCompletion } from '@api/filters';
import { debounce } from 'lodash';
import apptracker from '@utils/sensor/apptracker';
import { CREATE_BUTTON_ON_DIMENSION_MANAGEMENT } from '@src/utils/sensor/track';
import { useQuery } from '@tanstack/react-query';

const initialState = {
  customTagEnable: true,
  tagKeyEnable: true,
  createLoading: false,
  createMode: false,
};

function reducer(state: any, action: any) {
  switch (action.type) {
    case 'SET_CUSTOM_TAG_ENABLE':
      return { ...state, customTagEnable: action.payload };
    case 'SET_TAG_KEY_ENABLE':
      return { ...state, tagKeyEnable: action.payload };
    case 'SET_CREATE_LOADING':
      return { ...state, createLoading: action.payload };
    case 'TOGGLE_CREATE_MODE':
      return { ...state, createMode: !state.createMode };
    default:
      return state;
  }
}

export const DimensionCreatePanel = (prop: any) => {
  const { maxInfo } = prop;
  const [search, setSearch] = useState<string>('');
  const dimensionsData = useAtomValue(atoms.dimensionsData);
  const dimensionMetaList = useAtomValue(atoms.dimensionMeta);
  const callbacks = useAtomValue(atoms.callbacks);
  const [selectableDimension, setSelectableDimension] = useState<SelectItem[]>([]);

  const [customNameEnable, setCustomNameEnable] = useState<boolean>(true);
  const [tagKeyEnable, setTagKeyEnable] = useState<boolean>(true);
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [createMode, setCreateMode] = useState<boolean>(false);

  const searchedTags = useQuery(
    [search],
    async () => {
      try {
        const res = await getCustomTagAutoCompletion({
          params: {
            mode: 'ei',
            pattern: search,
          },
        });
        const tags = res.data || [];
        return tags;
      } catch {
        return [{ text: search, value: search }];
      }
    },
    {
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      enabled: (search?.length ?? 0) > 2,
    },
  );

  const options = React.useMemo(() => {
    const res =
      searchedTags.data?.map((it: any) => ({
        Label: it.text,
        value: it.value,
      })) ?? [];

    if (!res.find((it: any) => it.label === search) && search) {
      res.unshift({ label: search, value: search });
    }

    return res;
  }, [searchedTags.data, search]);

  useEffect(() => {
    const existingNames = new Set(dimensionsData.dimensions.map((s) => s.name));
    const existingTags = new Set(
      dimensionsData.dimensions.map((s) => s.tag_key).filter((key) => key), // Remove null values
    );
    const exitGroupBy = new Set(
      dimensionsData.dimensions
        .map((s) => s.group_by)
        .filter((group_by) => group_by && group_by !== Const.TAG_KEY),
    ); // Remove null values

    // get selectable dimension list from meta, existingNames includes such dimension,
    // then it won't show in the drop down list
    // also if tag_key is already used, it won't show in the drop down list
    // also if group_by name is already used, it won't show in the drop down list, otherwise it will
    // introduce duplicated group by name
    const mappedDimensions = dimensionMetaList
      .filter((dimension) => !existingNames.has(dimension.name))
      .filter((dimension) => !existingTags.has(dimension.tag_key))
      .filter((dimension) => !exitGroupBy.has(dimension.group_by))
      .map((item) => ({
        value: item.name,
        label: item.name,
        key: item.name,
      }));

    setSelectableDimension(mappedDimensions);
  }, [dimensionsData, dimensionMetaList]);

  const handleCreateModeToggle = () => {
    // Track the operation
    apptracker(CREATE_BUTTON_ON_DIMENSION_MANAGEMENT, { dimensionOperation: 'create clicked' });

    // Toggle the create mode
    setCreateMode((prevMode) => !prevMode);
  };

  const onFinish = async (values: CreateDimensionType) => {
    setCreateLoading(true);
    const isEIActive = values.selected_products?.includes('ei');
    const isAIActive = values.selected_products?.includes('ai');
    try {
      const selectedMeta = dimensionMetaList.find((item) => item.name === values.name);
      if (!selectedMeta) {
        message.error('Dimension not valid');
        return;
      }

      const inputDimension: Dimension = {
        name:
          values.name === Const.CUSTOM_DIMENSION ? values.custom_name.trim() : values.name.trim(),
        tag_key: selectedMeta.show_tag_field ? values.tag_key.trim() : selectedMeta.tag_key,
        group_by: values.name === Const.CUSTOM_DIMENSION ? Const.TAG_KEY : selectedMeta.group_by,
        // add default false, otherwise CCS will miss these 2 fields
        ei: isEIActive || false,
        ai: isAIActive || false,
      };

      const duplicateName = dimensionsData?.dimensions.find(
        (item) => !!item.name && item.name === inputDimension.name,
      );
      const duplicateTag = dimensionsData?.dimensions.find(
        (item) => !!item.tag_key && item.tag_key === inputDimension.tag_key,
      );

      if (duplicateName) {
        message.error(Const.DIMENSION_NAME_DUPLICATION(duplicateName.name));
        return;
      }

      if (duplicateTag) {
        message.error(Const.DIMENSION_TAG_DUPLICATION(duplicateTag.tag_key));
        return;
      }

      if (isEIActive && maxInfo.activeEIDimension >= maxInfo.maxEIDimension) {
        message.error(`Video ${Const.DIMENSION_EXCEED_ERROR} ${maxInfo.maxEIDimension}`);
        return;
      }

      if (isAIActive && maxInfo.activeAIDimension >= maxInfo.maxAIDimension) {
        message.error(`Ads ${Const.DIMENSION_EXCEED_ERROR} ${maxInfo.maxAIDimension}`);
        return;
      }

      await callbacks.onEdit?.({
        configurationVersion: dimensionsData.configurationVersion,
        configId: dimensionsData.configId,
        dimensions: [...dimensionsData.dimensions, inputDimension],
        operation: { dimensionOperation: CREATE_OPERATION, dimension: inputDimension },
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || Const.DIMENSION_CREATION_FAIL_GENERAL;
      message.error(errorMessage);
    } finally {
      form.resetFields();
      setCreateLoading(false);
      setCustomNameEnable(true);
      setTagKeyEnable(true);
    }
  };

  const handleSearch = React.useMemo(() => debounce((v: string) => setSearch(v.trim()), 500), []);

  const handleSelect = async (dimension: string) => {
    form.resetFields(['custom_name', 'tag_key']);
    if (dimension === 'Custom') {
      setCustomNameEnable(true);
      setTagKeyEnable(true);
    } else {
      setCustomNameEnable(false);
      const selected = dimensionMetaList.filter(
        (dimensionMeta) => dimensionMeta.name === dimension,
      );
      setTagKeyEnable(!!selected[0]?.show_tag_field);
    }
  };

  const [form] = Form.useForm();

  return createMode ? (
    <Style.DimensionCreateContainer>
      <Style.CloseCreationButton
        color="default"
        type="text"
        icon={<CloseOutlined />}
        onClick={() => {
          setCreateMode(!createMode);
          form.setFieldValue('name', Const.CUSTOM_DIMENSION);
          setCustomNameEnable(true);
          setTagKeyEnable(true);
        }}
      />
      <Tooltip
        title="Enable dimension for both Video & Ads to make it work for Ads metrics"
        placement="leftTop"
      >
        <Style.CreateButton>
          Create New Dimension <InfoCircleOutlined />
        </Style.CreateButton>
      </Tooltip>
      <Form
        form={form}
        disabled={createLoading}
        onFinish={onFinish}
        name="dimension_create_panel"
        layout="vertical"
        style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginLeft: '7px' }}
        initialValues={{ name: Const.CUSTOM_DIMENSION }} // Set default value for 'name'
      >
        <Form.Item
          name="name"
          rules={[{ required: true, message: Const.DIMENSION_REQUIRED_ERROR }]}
        >
          <Select
            showSearch
            placeholder={'Select dimension'}
            style={{ width: '180px' }}
            options={selectableDimension}
            onChange={handleSelect}
          ></Select>
        </Form.Item>

        <Form.Item
          name="custom_name"
          rules={[
            {
              required: form.getFieldValue('name') === Const.CUSTOM_DIMENSION,
              message: Const.CUSTOM_NAME_REQUIRED_ERROR,
            },
            {
              validator: (_, value) => {
                if (value && value.includes(Const.LEGACY)) {
                  return Promise.reject(new Error(Const.LEGACY_TEXT));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Style.DimensionCreateInput
            type="text"
            placeholder={'Custom Name'}
            disabled={!customNameEnable || createLoading}
          />
        </Form.Item>

        <Form.Item
          name="tag_key"
          rules={[
            {
              validator: (_, value) => {
                const selectedDimension = dimensionMetaList.find(
                  (dimensionMeta) => dimensionMeta.name === form.getFieldValue('name'),
                );

                if (!selectedDimension) {
                  return Promise.reject(new Error('Dimension not valid')); // Handle missing dimension
                }

                const trimValue = value?.trim();
                if (selectedDimension.show_tag_field && !trimValue) {
                  return Promise.reject(new Error(Const.TAG_KEY_REQUIRED_ERROR));
                }

                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (Const.BANNED_TAGS.includes(value)) {
                  notification.error({
                    message: 'Validation Error',
                    description: Const.INVALID_TAG_ERROR,
                    duration: 10,
                  });
                  return Promise.reject();
                }
                notification.destroy(); // notification will disappear if tag name not matched the BANNED_TAGS
                return Promise.resolve();
              },
            },
          ]}
        >
          <AutoComplete
            disabled={!tagKeyEnable || createLoading}
            options={options}
            onSearch={handleSearch}
            style={{ width: 180 }}
            placeholder="Tag Key"
            notFoundContent={searchedTags.isLoading ? <Style.DropdownSpin /> : null}
            popupRender={(menu) => (searchedTags.isLoading ? <Style.DropdownSpin /> : menu)}
          />
        </Form.Item>

        <Form.Item name="selected_products">
          <Select
            mode="multiple"
            placeholder={'Select product'}
            style={{ width: '210px' }}
            options={Const.OPTIONS}
          ></Select>
        </Form.Item>

        <Form.Item>
          <Button htmlType="submit" loading={createLoading}>
            Create
          </Button>
        </Form.Item>
      </Form>
    </Style.DimensionCreateContainer>
  ) : (
    <Style.StartCreationButton onClick={handleCreateModeToggle}>
      Create New Dimension
    </Style.StartCreationButton>
  );
};
